import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import PackagePhotoCapture from '../components/PackagePhotoCapture';
import { useVehicleTypes, vehicleDrawingUrl } from '../lib/useVehicleTypes';
import { useRegion } from '../lib/useHomeCountry';
import { phoneShape } from '../lib/phoneShape';
import './bookRide.css';

function BackArrow() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M15.5 18.5L8.5 12l7-7.5"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const SIZES = [
  { id: 'small', label: 'Small' },
  { id: 'medium', label: 'Medium' },
  { id: 'large', label: 'Large' },
  { id: 'xlarge', label: 'Extra Large' },
];

function IconEnvelope() {
  return (
    <svg viewBox="0 0 32 32" width="28" height="28" fill="none" aria-hidden>
      <rect x="3" y="6" width="26" height="20" rx="1.5" stroke="currentColor" strokeWidth="1.4" fill="none" />
      <path d="M3 8l13 8 13-8" stroke="currentColor" strokeWidth="1.2" fill="none" />
    </svg>
  );
}
function IconBoxSm() {
  return (
    <svg viewBox="0 0 32 32" width="28" height="28" fill="none" aria-hidden>
      <rect x="5" y="8" width="14" height="12" rx="1" stroke="currentColor" strokeWidth="1.3" fill="none" />
      <path d="M5 12h14" stroke="currentColor" strokeWidth="0.8" />
    </svg>
  );
}
function IconBoxLg() {
  return (
    <svg viewBox="0 0 32 32" width="28" height="28" fill="none" aria-hidden>
      <rect x="3" y="4" width="20" height="20" rx="1.2" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <path d="M3 10h20" stroke="currentColor" strokeWidth="0.8" />
    </svg>
  );
}
function IconPallet() {
  return (
    <svg viewBox="0 0 32 32" width="28" height="28" fill="none" aria-hidden>
      <rect x="2" y="18" width="28" height="4" rx="0.5" fill="currentColor" />
      <rect x="4" y="6" width="8" height="10" stroke="currentColor" strokeWidth="1" fill="none" />
      <rect x="20" y="6" width="8" height="10" stroke="currentColor" strokeWidth="1" fill="none" />
    </svg>
  );
}

const sizeIcon = {
  small: IconEnvelope,
  medium: IconBoxSm,
  large: IconBoxLg,
  xlarge: IconPallet,
};

const PACKAGE_TYPE_OPTIONS = [
  'Documents',
  'Electronics',
  'Clothing',
  'Food',
  'Fragile',
  'Other',
];

export default function PackageDetailsPage() {
  const navigate = useNavigate();
  // A direct open or a reload has null, not undefined: `= {}` would not replace it.
  const routeState = useLocation().state || {};
  const [size, setSize] = useState('medium');
  const [weight, setWeight] = useState('');
  const [typeCategory, setTypeCategory] = useState('Documents');
  const [typeOther, setTypeOther] = useState('');
  const existingPkg = routeState.package && typeof routeState.package === 'object' ? routeState.package : {};
  const [fileName, setFileName] = useState(typeof existingPkg.fileName === 'string' ? existingPkg.fileName : '');
  const [photoDataUrl, setPhotoDataUrl] = useState(
    typeof existingPkg.photoDataUrl === 'string' && existingPkg.photoDataUrl.startsWith('data:image/')
      ? existingPkg.photoDataUrl
      : null,
  );
  const [notes, setNotes] = useState('');
  // Who takes the parcel at the door: the rider's number to call there. Left
  // empty, the client who books is the contact at both ends.
  const [recipientName, setRecipientName] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  // Checked here, where it is typed: a digit missing or one too many was
  // refused only at booking, without saying whose number (live, 5 Oct 2026).
  const region = useRegion();
  const [phoneProblem, setPhoneProblem] = useState('');
  // The vehicle types the office keeps (Rio, 4 Oct 2026: "not limited to the
  // three kinds we have"), the first chosen until the client picks one.
  const { vehicles, loading: vehiclesLoading, failed: vehiclesFailed, retry: retryVehicles } = useVehicleTypes();
  const [vehicleKey, setVehicleKey] = useState('');
  const vehicle = vehicles.find((v) => v.key === vehicleKey) ?? vehicles[0] ?? null;
  const [saveError, setSaveError] = useState('');

  // The parcel travels with the booking, on the next step; nothing is written here.
  const onContinue = (e) => {
    e.preventDefault();
    setSaveError('');

    if (typeCategory === 'Other' && !typeOther.trim()) {
      setSaveError('Please describe your package type when you choose Other.');
      return;
    }
    if (!vehicle) {
      setSaveError('Choose the vehicle your parcel needs.');
      return;
    }
    const phone = phoneShape(recipientPhone, region);
    if (!phone.ok) {
      setPhoneProblem(phone.message);
      return;
    }

    const resolvedType = typeCategory === 'Other' ? typeOther.trim() : typeCategory;

    const pkg = {
      size,
      weight,
      type: resolvedType,
      // The kind as chosen, and its words for "Other": what InGo's booking takes.
      category: typeCategory,
      categoryOther: typeCategory === 'Other' ? typeOther.trim() : '',
      notes,
      recipientName: recipientName.trim(),
      recipientPhone: recipientPhone.trim(),
      fileName,
      photoDataUrl,
      // The type's own service is what the price and the booking name.
      vehicle: vehicle.key,
      serviceType: vehicle.serviceType,
      requestedVehicleType: vehicle.label,
    };

    navigate('/price-estimate', {
      state: {
        ...routeState,
        package: pkg,
      },
    });
  };

  return (
    <form id="pd-form" className="br-page br-page--form" onSubmit={onContinue}>
      <header className="br-nav br-nav--stacked">
        <Link to="/request-delivery" className="br-nav__back" aria-label="Back to request delivery">
          <BackArrow />
        </Link>
        <div className="br-nav__center">
          <h1 className="br-nav__title">Package Details</h1>
          <p className="br-nav__step">Step 2 of 3</p>
        </div>
        <span className="br-nav__spacer" aria-hidden />
      </header>

      <div className="br-scroll br-scroll--form">
        <section className="br-pd-card" aria-label="Package size">
          <h2 className="br-pd-heading">Package size</h2>
          <p className="br-pd-hint">Select one size for your parcel.</p>
          <div className="br-pd-grid br-pd-grid--4" role="radiogroup" aria-label="Package size">
            {SIZES.map((s) => {
              const IconC = sizeIcon[s.id] || IconBoxSm;
              const on = size === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  className={on ? 'br-tier br-tier--active br-pd-pick' : 'br-tier br-pd-pick'}
                  onClick={() => setSize(s.id)}
                  role="radio"
                  aria-checked={on}
                >
                  <span className="br-tier__icon br-pd-pick__icon">
                    <IconC />
                  </span>
                  <span className="br-tier__label">{s.label}</span>
                </button>
              );
            })}
          </div>
        </section>

        <section className="br-pd-card" aria-label="Vehicle size">
          <h2 className="br-pd-heading">Vehicle needed</h2>
          {/* A rider is offered only a delivery of their own vehicle (InGo's dispatch): never "or a larger one". */}
          <p className="br-pd-hint">Pick the vehicle your parcel needs. Riders on that vehicle can accept your delivery.</p>
          {vehiclesLoading ? <p className="br-pd-hint" role="status">Finding InGo&apos;s vehicles…</p> : null}
          {vehiclesFailed ? (
            <p className="br-pd-hint" role="alert">
              InGo&apos;s vehicles could not be loaded.{' '}
              <button type="button" className="br-pd-link" onClick={retryVehicles}>
                Try again
              </button>
            </p>
          ) : null}
          <div className="br-pd-grid br-pd-grid--3" role="radiogroup" aria-label="Vehicle for the delivery">
            {vehicles.map((v) => {
              const on = vehicle?.key === v.key;
              return (
                <button
                  key={v.key}
                  type="button"
                  className={on ? 'br-tier br-tier--active br-pd-pick' : 'br-tier br-pd-pick'}
                  onClick={() => setVehicleKey(v.key)}
                  role="radio"
                  aria-checked={on}
                >
                  <img src={vehicleDrawingUrl(v.drawing)} alt="" width="32" height="32" aria-hidden />
                  <span className="br-tier__label">{v.label}</span>
                </button>
              );
            })}
          </div>
        </section>

        <section className="br-pd-card">
          <h2 className="br-pd-heading">Package weight</h2>
          <div className="br-pd-field">
            <input
              className="br-pd-input"
              type="text"
              inputMode="decimal"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              placeholder="0.0"
              autoComplete="off"
              aria-label="Weight in kilograms"
            />
            <span className="br-pd-field__suffix">kg</span>
          </div>
        </section>

        <section className="br-pd-card">
          <h2 className="br-pd-heading">Package type</h2>
          <p className="br-pd-hint">
            Choose a category. If you pick Other, describe what you are sending below.
          </p>
          <div className="br-pd-select-wrap">
            <select
              className="br-pd-select"
              value={typeCategory}
              onChange={(e) => setTypeCategory(e.target.value)}
              aria-label="Package type category"
            >
              {PACKAGE_TYPE_OPTIONS.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
          {typeCategory === 'Other' ? (
            <div className="br-pd-field br-pd-field--mt">
              <input
                className="br-pd-input"
                type="text"
                value={typeOther}
                onChange={(e) => setTypeOther(e.target.value)}
                placeholder="e.g. Medical supplies, pet food, tools"
                autoComplete="off"
                maxLength={200}
                aria-label="Describe your package type"
              />
            </div>
          ) : null}
        </section>

        <section className="br-pd-card">
          <h2 className="br-pd-heading">Package photo</h2>
          <PackagePhotoCapture
            value={photoDataUrl}
            onChange={(url, name) => {
              setSaveError('');
              setPhotoDataUrl(url);
              setFileName(url ? name || 'package.jpg' : '');
            }}
            hint="Take or upload a photo of the parcel. The driver will see this at pickup."
          />
        </section>

        <section className="br-pd-card" aria-label="Who receives it">
          <h2 className="br-pd-heading">Who receives it</h2>
          <p className="br-pd-hint">The rider calls them at the door. Leave it empty if it is you.</p>
          <div className="br-pd-field">
            <input
              className="br-pd-input"
              type="text"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              placeholder="Their name"
              autoComplete="off"
              maxLength={120}
              aria-label="Recipient's name"
            />
          </div>
          <div className="br-pd-field br-pd-field--mt">
            <input
              className="br-pd-input"
              type="tel"
              inputMode="tel"
              value={recipientPhone}
              onChange={(e) => {
                setRecipientPhone(e.target.value);
                setPhoneProblem('');
              }}
              placeholder={region?.home?.phoneExample ? `Their phone, e.g. 0${region.home.phoneExample}` : 'Their phone'}
              autoComplete="off"
              maxLength={32}
              aria-label="Recipient's phone"
              aria-invalid={phoneProblem ? true : undefined}
              aria-describedby={phoneProblem ? 'pd-phone-problem' : undefined}
            />
          </div>
          {phoneProblem ? (
            <p className="br-pd-error" role="alert" id="pd-phone-problem">
              {phoneProblem}
            </p>
          ) : null}
        </section>

        <section className="br-pd-card">
          <h2 className="br-pd-heading">Special instructions</h2>
          <textarea
            className="br-pd-textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Any handling instructions?"
            autoComplete="off"
            maxLength={500}
          />
        </section>

        {saveError ? (
          <p className="br-pd-error" role="alert">
            {saveError}
          </p>
        ) : null}
      </div>

      <div className="br-footer">
        <button type="submit" className="br-confirm">
          Continue
        </button>
      </div>
    </form>
  );
}
